import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { AuditHistory, AuditHistoryRequest, AuditHistoryInput } from '../models/audit-history.model';
import { BaseRespone } from 'app/core/models/common.response';

/**
 * Service for fetching audit history data
 */
@Injectable({
    providedIn: 'root'
})
export class ReportHistoryService {
    
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Get audit history for an entity
     * 
     * @param input - The input parameters containing entityId and entityType
     * @param pageNumber - Optional page number (default: 0)
     * @param pageSize - Optional page size (default: 100)
     * @returns Observable of audit history records
     */
    getAuditHistory(
        input: AuditHistoryInput,
        pageNumber: number = 0,
        pageSize: number = 100
    ): Observable<AuditHistory[]> {
        // Build request body
        const requestBody: AuditHistoryRequest = {
            pageNumber: pageNumber,
            pageSize: pageSize,
            tableName: input.entityType,
            entityId: input.entityId
        };

        return this._httpClient
            .post<BaseRespone<any>>(`${environment.baseUrl}/Report/audit/history`, requestBody)
            .pipe(
                map(response => {
                    if (!response.isSuccess) {
                        throw new Error(response.message || 'Failed to fetch audit history');
                    }

                    const result = response.result;
                    
                    // Check if result is null or undefined
                    if (!result) {
                        return [];
                    }
                    
                    // Handle the API structure: result.auditLogs
                    if (result.auditLogs && Array.isArray(result.auditLogs)) {
                        return result.auditLogs.map((item: any) => ({
                            propertyName: item.propertyName || '',
                            oldValue: item.oldValue || null,
                            newValue: item.newValue || null,
                            changedByName: item.changedByName || '',
                            changedAt: item.changedAt // Keep as string from API
                        })) as AuditHistory[];
                    }
                    
                    // Fallback: Check if result is already an array
                    if (Array.isArray(result)) {
                        return result.map(item => ({
                            propertyName: item.propertyName || '',
                            oldValue: item.oldValue || null,
                            newValue: item.newValue || null,
                            changedByName: item.changedByName || '',
                            changedAt: item.changedAt // Keep as string from API
                        })) as AuditHistory[];
                    }
                    
                    return [];
                }),
                catchError(error => {
                    console.error('Error fetching audit history:', error);
                    const errorMessage = error?.error?.message || error?.message || 'Failed to fetch audit history. Please try again.';
                    return throwError(() => new Error(errorMessage));
                })
            );
    }
}
